import type { StreamStatus } from "../api/stream";
import { useBackend } from "../app/BackendProvider";
import { StreamBadge, Topbar, TopbarClock } from "../app/Topbar";
import { formatPercent } from "../app/format";
import { useNetworkId } from "../config/ConfigProvider";
import { useActivePostParam } from "../posts/PostProvider";
import { PostSelector } from "../posts/PostSelector";
import { usePolledResource } from "./hooks";

/**
 * Bandeau du cockpit (F1.1) : marque et textes pilotés serveur, onglets, KPI
 * ponctualité et compteur d'événements (calculés serveur, `/kpi`), horloge,
 * état du flux, poste actif.
 */
export function CockpitTopbar({ streamStatus }: { streamStatus: StreamStatus }) {
  const backend = useBackend();
  const networkId = useNetworkId();

  const postParam = useActivePostParam();
  const { data: kpi } = usePolledResource(
    (signal) => backend.api.getKpi(networkId, postParam ? { post: postParam } : {}, signal),
    [backend, networkId, postParam],
    20_000,
  );

  return (
    <Topbar
      current="cockpit"
      right={
        <>
          <Metric label="Ponct." value={kpi ? formatPercent(kpi.punctualityRatio, 1) : "—"} />
          <span style={{ display: "flex", alignItems: "center", gap: 7 }} title="Événements ouverts (alertes GTFS-RT)">
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f97316" }} aria-hidden />
            <span className="mono" style={{ fontSize: "0.78rem", fontWeight: 600, color: "#fff" }}>
              {kpi ? `${kpi.openAlerts} événement${kpi.openAlerts > 1 ? "s" : ""}` : "—"}
            </span>
          </span>
          <StreamBadge status={streamStatus} />
          <TopbarClock />
          <span style={{ filter: "invert(1)" }}>
            <PostSelector compact />
          </span>
        </>
      }
    />
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
      <span className="ovl" style={{ color: "hsla(0,0%,100%,.45)" }}>
        {label}
      </span>
      <span className="mono" style={{ fontSize: "0.9rem", fontWeight: 700, color: "#fff" }}>
        {value}
      </span>
    </span>
  );
}
